import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { clientDocumentAPI } from '../services/api';
import ContentHeader from '../components/ContentHeader';
import './ClientFolderTemplate.css';

/** Strip legacy ~ and file extensions; return base name only for display/editing/save */
function normalizeFileTemplate(s) {
  if (typeof s !== 'string') return '';
  let t = s.replace(/~/g, '').trim(); // remove all tildes
  t = t.replace(/\.[a-zA-Z0-9]{2,5}$/, '').trim(); // remove extension
  return t.trim();
}

/** Deep clone folder tree and normalize fileTemplates (remove tildes) for editing */
function foldersForEditing(folders) {
  if (!Array.isArray(folders)) return [];
  return folders.map((f) => ({
    key: f.key || '',
    name: f.name || '',
    order: typeof f.order === 'number' ? f.order : 0,
    fileTemplates: Array.isArray(f.fileTemplates)
      ? f.fileTemplates.map(normalizeFileTemplate).filter(Boolean)
      : [],
    children: foldersForEditing(f.children),
  }));
}

/** Build folder tree for save; fileTemplates stored as base names only (no extensions) */
function foldersForSave(folders) {
  if (!Array.isArray(folders)) return [];
  return folders.map((f) => ({
    key: f.key || '',
    name: f.name || '',
    order: typeof f.order === 'number' ? f.order : 0,
    fileTemplates: Array.isArray(f.fileTemplates)
      ? f.fileTemplates
          .filter((s) => s && String(s).trim())
          .map((s) => normalizeFileTemplate(String(s)))
          .filter(Boolean)
      : [],
    children: foldersForSave(f.children),
  }));
}

function slugFromName(name) {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'new-folder';
}

/**
 * Single folder editor: name (key is auto-generated, not shown), file name suggestions, children
 */
function FolderEditor({ folder, parentKey, onUpdate, onRemove, onAddChild, depth = 0 }) {
  const [expanded, setExpanded] = useState(depth < 1);
  const [name, setName] = useState(folder.name);
  const [fileTemplates, setFileTemplates] = useState(folder.fileTemplates || []);
  const hasChildren = folder.children && folder.children.length > 0;

  useEffect(() => {
    setName(folder.name);
    setFileTemplates(folder.fileTemplates || []);
  }, [folder.name, folder.fileTemplates]);

  const commitName = (newName) => {
    setName(newName);
    onUpdate({ ...folder, name: newName });
  };
  const commitFileTemplates = (newList) => {
    setFileTemplates(newList);
    onUpdate({ ...folder, fileTemplates: newList });
  };
  const addSuggestion = () => {
    commitFileTemplates([...fileTemplates, '']);
  };
  const updateSuggestion = (index, value) => {
    const next = [...fileTemplates];
    next[index] = value;
    commitFileTemplates(next);
  };
  const removeSuggestion = (index) => {
    commitFileTemplates(fileTemplates.filter((_, i) => i !== index));
  };
  const updateChild = (index, updatedChild) => {
    const next = [...(folder.children || [])];
    next[index] = updatedChild;
    onUpdate({ ...folder, children: next });
  };
  const removeChild = (index) => {
    const next = (folder.children || []).filter((_, i) => i !== index);
    onUpdate({ ...folder, children: next });
  };
  const addChild = () => {
    const baseKey = folder.key ? `${folder.key}/` : '';
    const existingKeys = (folder.children || []).map((c) => c.key);
    let candidate = `${baseKey}new-folder`;
    let n = 1;
    while (existingKeys.includes(candidate)) {
      candidate = `${baseKey}new-folder-${n++}`;
    }
    const newChild = {
      key: candidate,
      name: 'New folder',
      order: (folder.children || []).length + 1,
      fileTemplates: [],
      children: [],
    };
    onUpdate({ ...folder, children: [...(folder.children || []), newChild] });
    setExpanded(true);
  };

  return (
    <div className="client-folder-template-folder" style={{ marginLeft: depth * 16 }}>
      <div className="client-folder-template-folder-header">
        <button
          type="button"
          className="client-folder-template-expand"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {hasChildren ? (expanded ? '▼' : '▶') : '○'}
        </button>
        <div className="client-folder-template-folder-fields">
          <input
            type="text"
            className="client-folder-template-input client-folder-template-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={(e) => commitName(e.target.value)}
            placeholder="Folder name"
          />
        </div>
        <button
          type="button"
          className="client-folder-template-btn-remove"
          onClick={onRemove}
          title="Remove folder"
        >
          Remove
        </button>
      </div>

      {expanded && (
        <div className="client-folder-template-folder-body">
          <div className="client-folder-template-section">
            <label>Suggested standardized file names</label>
            <p className="client-folder-template-hint">Base name only; the file's extension is added when uploading. These appear as "Save as" suggestions in this folder.</p>
            <ul className="client-folder-template-suggestions">
              {fileTemplates.map((s, i) => (
                <li key={i}>
                  <input
                    type="text"
                    className="client-folder-template-input"
                    value={s}
                    onChange={(e) => updateSuggestion(i, e.target.value)}
                    placeholder="e.g. Referral form"
                  />
                  <button
                    type="button"
                    className="client-folder-template-btn-remove-inline"
                    onClick={() => removeSuggestion(i)}
                    title="Remove"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className="client-folder-template-btn-add" onClick={addSuggestion}>
              + Add suggestion
            </button>
          </div>

          <div className="client-folder-template-section">
            <label>Subfolders</label>
            {(folder.children || []).map((child, i) => (
              <FolderEditor
                key={child.key || i}
                folder={child}
                parentKey={folder.key}
                onUpdate={(updated) => updateChild(i, updated)}
                onRemove={() => removeChild(i)}
                onAddChild={addChild}
                depth={depth + 1}
              />
            ))}
            <button type="button" className="client-folder-template-btn-add" onClick={addChild}>
              + Add subfolder
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClientFolderTemplate() {
  const { tenantPath } = useTenantFromUrl();
  const settingsBack = tenantPath('/settings/staff');
  const [template, setTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const skipNextSaveRef = useRef(true);

  const loadTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await clientDocumentAPI.getTemplate();
      const t = res.data?.data;
      if (!t) {
        setError('No folder template found for this tenant.');
        return;
      }
      setTemplate(t);
      setTemplateName(t.name || '');
      setTemplateDescription(t.description || '');
      setFolders(foldersForEditing(t.template_structure?.folders || []));
      skipNextSaveRef.current = true;
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const updateFolder = (index, updated) => {
    setFolders((prev) => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  };
  const removeFolder = (index) => {
    setFolders((prev) => prev.filter((_, i) => i !== index));
  };
  const addRootFolder = () => {
    const existingKeys = folders.map((f) => f.key);
    let key = 'new-folder';
    let n = 1;
    while (existingKeys.includes(key)) {
      key = `new-folder-${n++}`;
    }
    const newFolder = {
      key,
      name: 'New folder',
      order: folders.length + 1,
      fileTemplates: [],
      children: [],
    };
    setFolders((prev) => [...prev, newFolder]);
  };

  // Auto-save when template name, description, or folders change (debounced)
  useEffect(() => {
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    if (!template) return;

    const t = setTimeout(async () => {
      setSaving(true);
      setSaveMessage(null);
      try {
        const structure = {
          ...(template?.template_structure || {}),
          version: template?.template_structure?.version || '1.0',
          folders: foldersForSave(folders),
        };
        await clientDocumentAPI.updateTemplate({
          name: templateName,
          description: templateDescription || undefined,
          templateStructure: structure,
        });
        setTemplate((prev) => (prev ? { ...prev, name: templateName, description: templateDescription, template_structure: structure } : null));
        setSaveMessage('Saved');
        setTimeout(() => setSaveMessage(null), 2000);
      } catch (err) {
        setSaveMessage(err.response?.data?.error || err.message || 'Failed to save');
        setTimeout(() => setSaveMessage(null), 3000);
      } finally {
        setSaving(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [templateName, templateDescription, folders]);

  if (loading) {
    return (
      <div className="client-folder-template-page">
        <ContentHeader
          title="Client Folder Template"
          backTo={settingsBack}
          backLabel="Back to Settings"
        />
        <div className="client-folder-template-loading">Loading...</div>
      </div>
    );
  }

  if (error && !template) {
    return (
      <div className="client-folder-template-page">
        <ContentHeader
          title="Client Folder Template"
          backTo={settingsBack}
          backLabel="Back to Settings"
        />
        <div className="client-folder-template-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="client-folder-template-page">
      <ContentHeader
        title="Client Folder Template"
        titleHint="Edit the folder structure and suggested file names used when uploading client documents. Changes save automatically after you edit—use Back to Settings (top or bottom) or another item in the Settings sidebar when you are done."
        backTo={settingsBack}
        backLabel="Back to Settings"
        actions={
          saveMessage && (
            <span className={saveMessage.startsWith('Failed') ? 'client-folder-template-status error' : 'client-folder-template-status'}>
              {saveMessage}
            </span>
          )
        }
      />

      <div className="client-folder-template-meta">
        <div className="client-folder-template-field">
          <label>Template name</label>
          <input
            type="text"
            className="client-folder-template-input"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g. CFC4 Document Folder Structure"
          />
        </div>
        <div className="client-folder-template-field">
          <label>Description (optional)</label>
          <input
            type="text"
            className="client-folder-template-input"
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            placeholder="Short description"
          />
        </div>
      </div>

      <div className="client-folder-template-folders-section">
        <h2>Folders</h2>
        {folders.map((folder, i) => (
          <FolderEditor
            key={folder.key || i}
            folder={folder}
            onUpdate={(updated) => updateFolder(i, updated)}
            onRemove={() => removeFolder(i)}
            onAddChild={() => {}}
            depth={0}
          />
        ))}
        <button type="button" className="client-folder-template-btn-add-root" onClick={addRootFolder}>
          + Add top-level folder
        </button>
      </div>

      <div className="client-folder-template-footer-actions">
        <Link to={settingsBack} className="btn-cancel">
          Back to Settings
        </Link>
      </div>
    </div>
  );
}
